import { useEffect, useRef, type ReactNode } from "react";
import clsx from "clsx";
import "./styles.scss";
import { isIOSDevice } from "../../utils/deviceDetection";
import { useNubankFeatureFlagsContext } from "../../utils/featureFlagsContext";

interface NubankSimpleModalProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  headerElement?: ReactNode;
  topLeftButton?: ReactNode;
  topRightButton?: ReactNode;
}

/**
 * Simple full-screen modal replacing MUI Dialog for iOS Safari/WKWebView.
 * No focus trap, no transitions, no portal — just CSS positioning.
 *
 * iOS jitter fix: intercepts touchstart on inputs and uses
 * focus({ preventScroll: true }) to prevent iOS from scrolling the viewport.
 * See IOS_KEYBOARD_FIX.md for the full explanation.
 */
export const NubankSimpleModal = ({
  open,
  onClose,
  children,
  className,
  headerElement,
  topLeftButton,
  topRightButton,
}: NubankSimpleModalProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { disableTouchFix } = useNubankFeatureFlagsContext();

  // iOS jitter fix: intercept touchstart on inputs, prevent default,
  // then manually focus with preventScroll:true. This stops iOS from
  // scrolling the viewport when an input receives focus.
  // Only applied on iOS — on Android, preventDefault() on touchstart
  // suppresses the click event chain that MUI components need.
  useEffect(() => {
    if (!open || disableTouchFix) return;

    if (!isIOSDevice()) return;

    const root = rootRef.current;
    if (!root) return;

    const onTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT"
      ) {
        e.preventDefault();
        target.focus({ preventScroll: true });
      }
    };

    root.addEventListener("touchstart", onTouchStart, { passive: false });

    return () => {
      root.removeEventListener("touchstart", onTouchStart);
    };
  }, [open, disableTouchFix]);

  if (!open) return null;

  return (
    <div ref={rootRef} className={clsx("nubank-simple-modal-root", className)}>
      <div className="nubank-simple-modal-backdrop" onClick={onClose} />
      <div className="nubank-simple-modal-container">
        {(headerElement || topLeftButton || topRightButton) && (
          <div className="nubank-simple-modal-header">
            <div className="nubank-simple-modal-top-button left">
              {topLeftButton}
            </div>
            {headerElement && (
              <div className="nubank-simple-modal-header-content">
                {headerElement}
              </div>
            )}
            <div className="nubank-simple-modal-top-button right">
              {topRightButton}
            </div>
          </div>
        )}
        <div className="nubank-simple-modal-content">{children}</div>
      </div>
    </div>
  );
};
